import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { ApiError, fileUrl } from "../../../shared/api/client";
import { AppBadge, AppButton, AppField, AppTextarea, ConfirmAction } from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import {
  DecisionLayout,
  DecisionPanel,
  DetailPanel,
  DetailRows,
  NotifyPreview,
  PhotoGrid,
  PhotoThumb,
  ReasonPicker,
} from "../../../shared/components/DecisionChrome";
import {
  IconCamera,
  IconCheck,
  IconError,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import {
  approveInstallation,
  getContractTierInfo,
  getInstallation,
  markTokenDelivered,
  requestInstallationCorrection,
} from "../api";
import {
  customerDisplayName,
  useCustomersMap,
  useDeviceSerialMap,
  useProductsMap,
  useUserNameMap,
} from "../enrichment";
import {
  INSTALLATION_CORRECTION_REASON_LABELS,
  type InstallationCorrectionReason,
} from "../types";
import { KeyApprovalProcessStrip } from "../components/KeyApprovalProcessStrip";

/**
 * S7 — Key approval & first token (handoff TASK-008, s7-key-approval-*).
 * Left: evidence review (photos, GPS-vs-survey, payment, confirmation line,
 * GPS map placeholder). Right: decision card — Approve (single primary)
 * issues the first OpenPAYGO token ONCE (full value shown in the dark token
 * panel, grouped monospace digits + copy); Mark-as-delivered posts
 * token-delivered. Request correction uses the canonical reason list ('other'
 * requires a note) with the technician notification preview.
 * 503 = device-service unreachable, NO state change (distinct retry message);
 * 409 = already decided (graceful notice). Approve is idempotent — replaying
 * an approved installation returns the same token ("Show first token").
 */

const GEOFENCE_LIMIT_M = 3;

/** Design order (s7 boards): Photo, GPS, Serial, Payment, Other. */
const REASON_ORDER: InstallationCorrectionReason[] = [
  "photo_unclear",
  "gps_mismatch",
  "serial_mismatch",
  "payment_unverified",
  "other",
];

function tzs(n: number): string {
  return `TZS ${new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 0 }).format(n)}`;
}

function formatWhen(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

/** Group token digits in 3s for readability ("134 578 210 094 221"). */
function groupDigits(token: string): string {
  return token.replace(/(\d{3})(?=\d)/g, "$1 ");
}

export function S7KeyApproval() {
  const { id = "" } = useParams();
  const installationId = Number(id);
  const navigate = useNavigate();

  const [mode, setMode] = useState<"decide" | "correction">("decide");
  const [reasons, setReasons] = useState<InstallationCorrectionReason[]>([]);
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);

  const query = useQuery({
    queryKey: ["installation", installationId],
    queryFn: () => getInstallation(installationId),
    retry: 1,
    staleTime: 15_000,
  });

  const inst = query.data?.installation;

  const customers = useCustomersMap(useMemo(() => [inst?.customer_id ?? 0], [inst?.customer_id]));
  const technicians = useUserNameMap();
  const serials = useDeviceSerialMap();
  const products = useProductsMap(useMemo(() => [inst?.product_id ?? null], [inst?.product_id]));

  const tierQuery = useQuery({
    queryKey: ["inst-tier-info", inst?.contract_id ?? 0],
    queryFn: () => getContractTierInfo(inst!.contract_id!),
    enabled: inst?.contract_id != null,
    retry: 1,
    staleTime: 30_000,
  });

  const approve = useMutation({
    mutationFn: () => approveInstallation(installationId),
  });

  const delivered = useMutation({
    mutationFn: () => markTokenDelivered(installationId),
  });

  const correction = useMutation({
    mutationFn: (body: { reasons: InstallationCorrectionReason[]; note?: string }) =>
      requestInstallationCorrection(installationId, body),
  });

  const copyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
    } catch {
      // Clipboard API unavailable (non-secure context) — textarea fallback.
      const ta = document.createElement("textarea");
      ta.value = token;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  // ── Loading / error ────────────────────────────────────────────────────────
  if (query.isPending) {
    return <PageState status="loading" />;
  }

  if (query.isError || !inst) {
    return (
      <PageState
        status="error"
        error={query.error instanceof Error ? query.error.message : "Could not load installation"}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const customer = customers.get(inst.customer_id);
  const name = customerDisplayName(customer, inst.customer_id);
  const serial = inst.device_id != null ? (serials.get(inst.device_id) ?? "—") : "—";
  const technicianName =
    inst.technician_id != null ? (technicians.get(inst.technician_id) ?? `#${inst.technician_id}`) : "—";
  const productName = inst.product_id != null ? (products.get(inst.product_id) ?? "—") : "—";
  const dist = inst.survey_distance_m;

  // ── Token issued (approve succeeded — incl. idempotent replay) ────────────
  if (approve.isSuccess) {
    const data = approve.data;
    const deliveredAt =
      delivered.data?.installation.first_token_delivered_at ?? data.installation.first_token_delivered_at;
    return (
      <>
        <PageHeader
          title={`First key issued — ${name}`}
          subtitle="Office Key Approval issued this unlock. Deliver the code to the customer, then mark delivered. Village Chairman is not in this path."
          crumbs={[{ label: "Key Approvals", to: "/key-approvals" }, { label: "First key issued" }]}
        />

        <KeyApprovalProcessStrip activeStep={2} />

        <Paper
          elevation={0}
          sx={{
            bgcolor: "#0f172a",
            color: "#f1f5f9",
            borderRadius: 2,
            p: { xs: 3, sm: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1.5,
            textAlign: "center",
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.75 }}>
            First key — OpenPAYGO token ({data.reference})
          </Typography>
          <Typography
            sx={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: 2,
              wordSpacing: "6px",
              overflowWrap: "anywhere",
            }}
            aria-label="First token"
          >
            {groupDigits(data.first_token)}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "#fbbf24" }}>
            valid {data.valid_days} days
          </Typography>
          <AppButton
            variant="secondary"
            type="button"
            onClick={() => void copyToken(data.first_token)}
            style={{ borderColor: "rgba(241, 245, 249, 0.4)", color: "#f1f5f9" }}
          >
            {copied ? (
              <>
                <IconCheck size={14} /> Copied
              </>
            ) : (
              "Copy token"
            )}
          </AppButton>
        </Paper>

        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {data.replayed
            ? "This installation was already approved — the same token is shown again (idempotent replay). The full token is only visible on this screen."
            : `Shown once in full — after this only the masked token (${data.masked_token}) is stored. Share it with the customer and mark it delivered.`}
        </Typography>

        {delivered.isError ? (
          <Alert severity="error" icon={<IconError size={14} />} sx={{ mt: 2 }}>
            {delivered.error instanceof Error
              ? delivered.error.message
              : "Could not mark as delivered — retry."}
          </Alert>
        ) : null}

        <Stack spacing={1.5} sx={{ maxWidth: 480, mt: 2 }}>
          {delivered.isSuccess || deliveredAt ? (
            <>
              <Alert severity="success" icon={<IconCheck size={14} />}>
                Token delivered to customer
                {deliveredAt ? ` (${formatWhen(deliveredAt)})` : ""}
              </Alert>
              <AppButton variant="secondary" type="button" onClick={() => navigate("/key-approvals")}>
                Back to queue
              </AppButton>
            </>
          ) : (
            <AppButton type="button" onClick={() => delivered.mutate()} disabled={delivered.isPending}>
              {delivered.isPending ? <IconSpinner size={14} /> : null}
              Mark as delivered to customer
            </AppButton>
          )}
        </Stack>
      </>
    );
  }

  // ── Correction sent ────────────────────────────────────────────────────────
  if (correction.isSuccess) {
    return (
      <Stack spacing={2} sx={{ py: 4, alignItems: "center", textAlign: "center" }}>
        <IconCheck size={40} />
        <Typography variant="h2">Correction requested</Typography>
        <Typography color="text.secondary">
          The technician will be notified with the reasons and your note.
        </Typography>
        <AppButton type="button" onClick={() => navigate("/key-approvals")}>
          Back to queue
        </AppButton>
      </Stack>
    );
  }

  // ── Decision-card error handling ───────────────────────────────────────────
  const approveErr = approve.error instanceof ApiError ? approve.error : null;
  const correctionErr = correction.error instanceof ApiError ? correction.error : null;
  const decisionErr = mode === "correction" ? correctionErr : approveErr;
  const deviceUnreachable =
    decisionErr?.status === 503 || decisionErr?.details?.device_unreachable === true;
  const alreadyDecided = decisionErr?.status === 409;

  const submitCorrection = () => {
    setFormError(null);
    if (reasons.length === 0) {
      setFormError("Select at least one reason");
      return;
    }
    if (reasons.includes("other") && !note.trim()) {
      setFormError("A note is required when the reason is 'Other'");
      return;
    }
    correction.mutate({ reasons, note: note.trim() || undefined });
  };

  const toggleReason = (r: InstallationCorrectionReason) =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const busy = approve.isPending || correction.isPending;
  const decided = inst.status !== "pending_approval";

  const badge =
    inst.status === "pending_approval" ? (
      <AppBadge tone="warning">
        <IconWarning size={12} /> Pending
      </AppBadge>
    ) : inst.status === "approved" ? (
      <AppBadge tone="success">
        <IconCheck size={12} /> Approved
      </AppBadge>
    ) : inst.status === "correction_requested" ? (
      <AppBadge tone="info">Correction requested</AppBadge>
    ) : (
      <AppBadge tone="neutral">{inst.status}</AppBadge>
    );

  return (
    <>
      <PageHeader
        title={`Key Approval — ${name}`}
        subtitle="Approve to issue the installation first token (business/commercial/system admin). Not Village Chairman approval."
        crumbs={[{ label: "Key Approvals", to: "/key-approvals" }, { label: "Decision" }]}
      />

      <KeyApprovalProcessStrip
        activeStep={
          inst.status === "approved" || inst.first_token_issued_at
            ? 2
            : inst.status === "pending_approval"
              ? 1
              : 0
        }
      />

      <DecisionLayout
        detail={
          <DetailPanel title={name} badge={badge} aria-label="Installation evidence">
            <DetailRows
              rows={[
                {
                  label: "Contract",
                  value: tierQuery.data
                    ? `${tierQuery.data.contract_number} · ${tierQuery.data.tier.name}`
                    : (customer?.active_contract?.contract_number ??
                      (inst.contract_id ? `#${inst.contract_id}` : "—")),
                },
                { label: "Device", value: productName },
                { label: "Serial", value: serial },
                { label: "Technician", value: technicianName },
                {
                  label: "GPS vs survey",
                  value: (
                    <Stack
                      direction="row"
                      spacing={1}
                      useFlexGap
                      sx={{ justifyContent: { sm: "flex-end" }, flexWrap: "wrap", alignItems: "center" }}
                    >
                      <Typography variant="body2" component="span">
                        {dist !== null ? `${dist.toFixed(1)} m` : "—"}
                      </Typography>
                      {dist !== null ? (
                        dist <= GEOFENCE_LIMIT_M ? (
                          <AppBadge tone="success">
                            <IconCheck size={12} /> In range
                          </AppBadge>
                        ) : (
                          <AppBadge tone="warning">
                            <IconWarning size={12} /> Out of range
                          </AppBadge>
                        )
                      ) : null}
                    </Stack>
                  ),
                },
                {
                  label: "Deposit",
                  value: tierQuery.data
                    ? `${tzs(tierQuery.data.status.amount_paid)} — ledger verified`
                    : (customer?.active_contract ? `${tzs(customer.active_contract.amount_paid)}` : "—"),
                },
                {
                  label: "Submitted",
                  value: `${formatWhen(inst.created_at)} · INST-${inst.id}`,
                },
              ]}
            />

            {inst.customer_confirmed ? (
              <Alert severity="success" icon={<IconCheck size={14} />} sx={{ mb: 2 }}>
                Customer confirmed
                {inst.customer_confirmed_at ? ` ${formatWhen(inst.customer_confirmed_at)}` : ""} —{" "}
                {inst.confirmation_method === "pin" ? "PIN" : "signature on device"}
              </Alert>
            ) : null}

            <PhotoGrid>
              <PhotoBox
                label="Installed device"
                url={inst.photos?.find((p) => p.photo_type === "installed_device")?.photo_url}
              />
              <PhotoBox
                label="Serial close-up"
                url={inst.photos?.find((p) => p.photo_type === "serial_closeup")?.photo_url}
              />
            </PhotoGrid>

            {/* Map is a placeholder per handoff — no map dependency in this build. */}
            <Box
              role="img"
              aria-label="GPS map placeholder"
              sx={{
                mt: 2,
                minHeight: 140,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.default",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "text.secondary",
                textAlign: "center",
                p: 2,
              }}
            >
              <IconCamera size={18} />
              <Typography variant="body2">
                GPS map — survey pin vs capture pin
                {dist !== null ? ` (${dist.toFixed(1)} m apart)` : ""}
              </Typography>
            </Box>
          </DetailPanel>
        }
        decision={
          <DecisionPanel aria-label="Decision">
            {deviceUnreachable ? (
              <>
                <Alert severity="error" icon={<IconError size={14} />}>
                  Device service unreachable — nothing was approved and no token was issued. Retry
                  when the device service is back.
                </Alert>
                <AppButton
                  variant="secondary"
                  type="button"
                  onClick={() => approve.mutate()}
                  disabled={busy}
                  style={{ width: "100%" }}
                >
                  {approve.isPending ? <IconSpinner size={14} /> : null} Retry approval
                </AppButton>
              </>
            ) : alreadyDecided ? (
              <>
                <Alert severity="warning" icon={<IconWarning size={14} />}>
                  {decisionErr?.message ?? "This installation was already decided."}
                </Alert>
                <AppButton
                  variant="secondary"
                  type="button"
                  onClick={() => navigate("/key-approvals")}
                  style={{ width: "100%" }}
                >
                  Back to queue
                </AppButton>
              </>
            ) : decided ? (
              <>
                {inst.status === "approved" ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Approved {formatWhen(inst.approved_at)}. The first token can be shown again —
                      the server replays the same token idempotently.
                    </Typography>
                    {inst.first_token_delivered_at ? (
                      <Alert severity="success" icon={<IconCheck size={14} />}>
                        Token delivered {formatWhen(inst.first_token_delivered_at)}
                      </Alert>
                    ) : null}
                    <AppButton
                      type="button"
                      onClick={() => approve.mutate()}
                      disabled={busy}
                      style={{ width: "100%" }}
                    >
                      {approve.isPending ? <IconSpinner size={14} /> : null} Show first token
                    </AppButton>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      This installation is not awaiting approval (status: {inst.status}).
                    </Typography>
                    <AppButton
                      variant="secondary"
                      type="button"
                      onClick={() => navigate("/key-approvals")}
                      style={{ width: "100%" }}
                    >
                      Back to queue
                    </AppButton>
                  </>
                )}
              </>
            ) : (
              <>
                {approve.isError && !deviceUnreachable && !alreadyDecided ? (
                  <Alert severity="error" icon={<IconError size={14} />}>
                    Approval failed — {approveErr?.message ?? "network error"}. Nothing was
                    recorded; retry.
                  </Alert>
                ) : null}
                {correction.isError && !alreadyDecided ? (
                  <Alert severity="error" icon={<IconError size={14} />}>
                    Correction could not be sent — {correctionErr?.message ?? "network error"}.
                    Your note is preserved.
                  </Alert>
                ) : null}

                <AppButton
                  type="button"
                  onClick={() => setConfirmApprove(true)}
                  disabled={busy || mode === "correction"}
                  style={{ width: "100%" }}
                >
                  {approve.isPending ? <IconSpinner size={14} /> : null}
                  Approve — issue first key
                </AppButton>

                {mode === "decide" ? (
                  <AppButton
                    variant="secondary"
                    type="button"
                    className="warning"
                    onClick={() => setMode("correction")}
                    disabled={busy}
                    style={{ width: "100%" }}
                  >
                    Request correction
                  </AppButton>
                ) : (
                  <>
                    <AppField label="Reason">
                      <ReasonPicker
                        options={REASON_ORDER}
                        value={reasons}
                        onToggle={toggleReason}
                        labels={INSTALLATION_CORRECTION_REASON_LABELS}
                        aria-label="Correction reasons"
                      />
                    </AppField>

                    <AppField
                      label={
                        reasons.includes("other")
                          ? "Note to technician (required)"
                          : "Note to technician"
                      }
                    >
                      <AppTextarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What should the technician fix?"
                      />
                    </AppField>

                    <NotifyPreview title="Notification preview (to technician)">
                      Correction requested for installation INST-{inst.id}:{" "}
                      {reasons.length > 0
                        ? reasons.map((r) => INSTALLATION_CORRECTION_REASON_LABELS[r]).join(", ")
                        : "…"}
                      {note.trim() ? ` — ${note.trim()}` : ""}
                    </NotifyPreview>

                    {formError ? (
                      <Alert severity="warning" icon={<IconWarning size={14} />}>
                        {formError}
                      </Alert>
                    ) : null}

                    {/* Correction send: warning-action tone per design. */}
                    <AppButton
                      type="button"
                      className="warning"
                      onClick={submitCorrection}
                      disabled={busy}
                      style={{ width: "100%" }}
                    >
                      {correction.isPending ? <IconSpinner size={14} /> : null}
                      Send Correction Request
                    </AppButton>
                    <AppButton
                      variant="secondary"
                      type="button"
                      onClick={() => setMode("decide")}
                      disabled={busy}
                      style={{ width: "100%" }}
                    >
                      Back
                    </AppButton>
                  </>
                )}

                {mode === "decide" ? (
                  <Typography variant="body2" color="text.secondary">
                    Approving issues the first OpenPAYGO token (full value shown once) and unlocks
                    the device.
                  </Typography>
                ) : null}
              </>
            )}
          </DecisionPanel>
        }
      />

      <ConfirmAction
        open={confirmApprove}
        title="Approve installation & issue first key?"
        message="This issues the first OpenPAYGO token (shown once) and unlocks the device. Confirm only when evidence and payment gates are satisfied."
        confirmLabel="Approve & issue key"
        danger
        busy={approve.isPending}
        onCancel={() => setConfirmApprove(false)}
        onConfirm={() => {
          setConfirmApprove(false);
          approve.mutate();
        }}
      />
    </>
  );
}
function PhotoBox({ label, url }: { label: string; url?: string | null }) {
  const [failed, setFailed] = useState(false);

  if (url && !failed) {
    const href = fileUrl(url);
    return (
      <Box component="span" onErrorCapture={() => setFailed(true)} sx={{ display: "contents" }}>
        <PhotoThumb label={label} href={href} />
      </Box>
    );
  }
  return (
    <PhotoThumb
      label={label}
      failedFallback={
        <Stack spacing={0.5} sx={{ alignItems: "center" }}>
          <IconCamera size={16} />
          <span>{label}</span>
        </Stack>
      }
    />
  );
}
